import { Fragment, useState } from 'react';
import type { CSSProperties, ReactNode, Ref } from 'react';
import { CHRONICLE_LOOK, ERA_LOOKS, eraPartIndex, eraSteps, stopSlides } from '@continuum/show-core';
import type { Chapter, EraLook, Phase, StopStep } from '@continuum/show-core';
import { BannerHost, SheetLayerProvider } from './OverlayHost.js';
import type { Lang } from './text.js';

const mono = "'IBM Plex Mono', monospace";

interface ShellProps {
  look: EraLook;
  /** Who this phone is: the call sign (once the room has it) and the seat. */
  callsign: string;
  seatLabel: string;
  charge: number;
  /** Where the show is, for the voyage bar under the header: the era, its part, and the stop's step. */
  chapter: number;
  phase: string;
  stop?: StopStep | null;
  /** NO ↔ EN, in the header on every screen, all evening (Marek, 2026-10-09). */
  lang?: Lang;
  onLang?: (l: Lang) => void;
  /** A row under the header for the game's controls: damage, CONTROLS, voted out, scores. */
  toolbar?: ReactNode;
  /** In the page flow under the screen: the table game's card and the team bar. */
  dock?: ReactNode;
  /** A button in the header, beside who this phone is: the messages (chat/). */
  action?: ReactNode;
  children: ReactNode;
}

/**
 * The phone frame: the ship header with the live charge bar, the voyage bar, the toolbar, the
 * screen, and the dock. Nothing here pretends to be the phone's own status bar or home bar;
 * the real ones are left alone and only their safe areas are kept clear.
 *
 * Everything is in the page flow, one under the other, so nothing in the frame ever lies on
 * top of the screen. Only pop-ups do that: banners at the top, and sheets (and a game) on a
 * layer over the whole frame.
 */
export function Shell({ look, callsign, seatLabel, charge, chapter, phase, stop = null, lang, onLang, toolbar, dock, action, children }: ShellProps) {
  const [layer, setLayer] = useState<HTMLDivElement | null>(null);

  return (
    <SheetLayerProvider value={layer}>
      <div
        className="cc-shell cc-ui"
        style={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          background: '#0a0e14',
          overflow: 'hidden',
        }}
      >
        <header style={header(look)}>
          <div
            style={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              border: `2px solid ${look.acc}`,
              flex: 'none',
            }}
          />
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.15, minWidth: 0, flex: '1 1 auto' }}>
            <span style={{ fontWeight: 700, fontSize: 14, letterSpacing: 2.5 }}>SS AURORA</span>
            {/* The era line may wrap to two lines on a small phone; it is never cut short. */}
            <span style={{ fontFamily: mono, fontSize: 11, letterSpacing: 0.8, lineHeight: 1.3, opacity: 0.75 }}>
              {/* Breaks only between the parts ("ERA 2 ·" / "THE BOOM ·" / "2213"), never inside a name. */}
              {look.tag.split(' · ').map((part, i, all) => (
                <Fragment key={i}>
                  <span style={{ whiteSpace: 'nowrap' }}>
                    {part}
                    {i < all.length - 1 ? ' ·' : ''}
                  </span>
                  {i < all.length - 1 ? ' ' : ''}
                </Fragment>
              ))}
            </span>
          </div>
          {lang && onLang ? <LangToggle lang={lang} onLang={onLang} colour={look.fg} /> : null}
          {action}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3, flex: 'none', maxWidth: '40%' }}>
            {callsign && (
              <span style={{ ...ellipsis, maxWidth: '100%', fontWeight: 700, fontSize: 14, letterSpacing: 1.5, lineHeight: 1.1 }}>{callsign}</span>
            )}
            <span style={{ ...ellipsis, maxWidth: '100%', fontFamily: mono, fontSize: 11, letterSpacing: 1, opacity: 0.8 }}>{seatLabel}</span>
            <div
              style={{
                width: 92,
                height: 4,
                background: 'rgba(128,128,128,.25)',
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${charge}%`,
                  background: look.acc,
                  transition: 'width .8s',
                }}
              />
            </div>
          </div>
        </header>
        <VoyageBar chapter={chapter} phase={phase} stop={stop} background={look.bg} />
        {toolbar && (
          <div
            style={{
              flex: 'none',
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 6,
              padding: '4px 10px',
              minHeight: 52,
              boxSizing: 'border-box',
              background: look.bg,
              color: look.fg,
              borderBottom: '1px solid rgba(255,255,255,.08)',
            }}
          >
            {toolbar}
          </div>
        )}

        <main style={{ flex: '1 1 auto', minHeight: 0, position: 'relative', overflow: 'hidden' }}>{children}</main>

        {dock && <div style={{ flex: 'none', background: '#07080c' }}>{dock}</div>}
        <div style={{ flex: 'none', height: 'env(safe-area-inset-bottom)', background: dock ? '#07080c' : look.bg }} />

        {/* Sheets and a full-screen game are drawn here, over the whole frame. */}
        <div ref={setLayer} style={{ position: 'absolute', inset: 0, zIndex: 50, pointerEvents: 'none' }} />
        {/* Banners drop in at the very top, over everything, like an iPhone's notifications. */}
        <BannerHost />
      </div>
    </SheetLayerProvider>
  );
}

const ellipsis: CSSProperties = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };

/** Boarding, the five eras and the end, in their own colours. */
const BANDS = ['#4fd8c2', ERA_LOOKS[1].acc, ERA_LOOKS[2].acc, ERA_LOOKS[3].acc, ERA_LOOKS[4].acc, ERA_LOOKS[5].acc, CHRONICLE_LOOK.acc];

/**
 * The switch between the two languages, as small as the chat button beside it (Marek, 2026-10-09: a guest
 * who chose English could not get back to Norwegian): it shows the language a tap switches to, so the
 * header still fits a small Android at 360 px.
 */
function LangToggle({ lang, onLang, colour }: { lang: Lang; onLang: (l: Lang) => void; colour: string }) {
  const other: Lang = lang === 'en' ? 'no' : 'en';
  return (
    <button
      onClick={() => onLang(other)}
      aria-label={lang === 'en' ? 'Norsk' : 'English'}
      title={lang === 'en' ? 'Norsk' : 'English'}
      style={{
        flex: 'none',
        width: 44,
        height: 44,
        borderRadius: 22,
        padding: 0,
        border: '1px solid rgba(255,255,255,.22)',
        background: 'transparent',
        color: colour,
        fontFamily: mono,
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: 1,
        cursor: 'pointer',
      }}
    >
      {other.toUpperCase()}
    </button>
  );
}

/**
 * The whole voyage: one band per era, one tick per part of it — the stop's own steps after its games
 * included (Marek, 2026-10-09: "all steps … on the progress bars … on the mobile app") — filling in as
 * the show runs. One tick for all of a stop's alarms: how many there are would tell how many saboteurs.
 */
function VoyageBar({ chapter, phase, stop, background }: { chapter: number; phase: string; stop: StopStep | null; background: string }) {
  const docked = phase === 'farewell';
  const at = Math.max(0, Math.min(5, chapter));
  const parts = eraSteps(at as Chapter).length;
  const stopAt = at >= 1 && phase === 'fieldwork' && stop && stop !== 'charged' ? stopSlides(at as Chapter).findIndex((x) => x.step === stop) : -1;
  const lit = stopAt >= 0 ? parts + stopAt : eraPartIndex(at as Chapter, phase as Phase);
  return (
    <div style={{ flex: 'none', display: 'flex', gap: 3, padding: '5px 16px 6px', background }}>
      {BANDS.map((colour, i) => {
        const count = i === 6 ? 1 : eraSteps(i as Chapter).length + (i >= 1 ? stopSlides(i as Chapter).length : 0);
        const here = i === at && !docked ? lit : -1;
        return (
          <div key={i} style={{ flex: i === 6 ? 0.5 : count, display: 'flex', gap: 1.5 }}>
            {Array.from({ length: count }, (_, j) => {
              const on = i === 6 ? docked : i < at || (i === at && (docked || (here >= 0 && j <= here)));
              return (
                <span
                  key={j}
                  style={{
                    flex: 1,
                    height: 4,
                    borderRadius: 2,
                    background: on ? colour : `${colour}33`,
                    transition: 'background-color .5s ease',
                  }}
                />
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

function header(look: EraLook): CSSProperties {
  return {
    flex: 'none',
    minHeight: 52,
    boxSizing: 'content-box',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '6px 16px',
    paddingTop: 'calc(env(safe-area-inset-top) + 6px)',
    background: look.bg,
    color: look.fg,
    borderBottom: '1px solid rgba(255,255,255,.08)',
  };
}

/**
 * Every screen sits in this frame. The content scrolls; `footer` is pinned under it, in the
 * flow, so the main button is always on screen and never lies over anything.
 */
export function Screen({
  children,
  style,
  footer,
  scrollRef,
}: {
  children: ReactNode;
  style?: CSSProperties;
  footer?: ReactNode;
  scrollRef?: Ref<HTMLDivElement>;
}) {
  const { padding = '28px 24px', ...outer } = style ?? {};
  return (
    <section
      style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        ...outer,
      }}
    >
      <div
        ref={scrollRef}
        style={{
          flex: '1 1 auto',
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          overscrollBehavior: 'contain',
          WebkitOverflowScrolling: 'touch',
          display: 'flex',
          flexDirection: 'column',
          padding,
          boxSizing: 'border-box',
        }}
      >
        {children}
      </div>
      {footer && (
        <div
          style={{
            flex: 'none',
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            padding: '12px 20px 14px',
            boxShadow: '0 -10px 18px -12px rgba(0,0,0,.7)',
            borderTop: '1px solid rgba(255,255,255,.06)',
          }}
        >
          {footer}
        </div>
      )}
    </section>
  );
}
